/* Progressive enhancement: all methods, paths and video links remain available without JS. */
(() => {
  'use strict';
  if (window.hcsCookingGuidesInitialized) return;
  window.hcsCookingGuidesInitialized = true;
  const initialize = (root) => {
    if (root.dataset.guideInitialized) return;
    root.dataset.guideInitialized = 'true';
    const abort = new AbortController();
    const listen = (element, type, handler, options = {}) => element.addEventListener(type, handler, { ...options, signal: abort.signal });
    const all = (selector) => [...root.querySelectorAll(selector)];
    const methods = all('.method');
    const methodButtons = all('[data-method]');
    const paths = all('.cg-path');
    const pathButtons = all('[data-path]');
    const rail = root.querySelector('.equipment-rail');
    const find = (id) => all('[id]').find((element) => element.id === id);
    const revealEquipment = (button) => {
      if (!rail || !button) return;
      const left = button.getBoundingClientRect().left - rail.getBoundingClientRect().left + rail.scrollLeft;
      const right = left + button.offsetWidth;
      let target = rail.scrollLeft;
      if (left < target) target = left - 3;
      else if (right > target + rail.clientWidth) target = right - rail.clientWidth + 3;
      rail.scrollTo({ left: target, behavior: 'instant' });
    };
    const selectMethod = (id) => {
      if (!methods.some((method) => method.id === id)) return;
      methods.forEach((method) => { method.hidden = method.id !== id; });
      methodButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.method === id)));
      revealEquipment(methodButtons.find((button) => button.dataset.method === id));
    };
    const choosePath = (id) => {
      if (!paths.some((path) => path.id === id)) return;
      paths.forEach((path) => { path.hidden = path.id !== id; });
      pathButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.path === id)));
    };
    const hashTarget = () => {
      try { return find(decodeURIComponent(location.hash.slice(1))); } catch { return null; }
    };
    const openTarget = (target, focus = false, smooth = false) => {
      if (target.matches('.method')) selectMethod(target.id);
      if (target.matches('.cg-path')) choosePath(target.id);
      if (target.matches('details')) target.open = true;
      const parentDetails = target.closest('details');
      if (parentDetails) parentDetails.open = true;
      const destination = target.matches('.cg-hidden-anchor') ? target.closest('section') : target;
      if (focus) {
        const temporary = !destination.matches('a,button,input,summary,[tabindex]');
        if (temporary) destination.setAttribute('tabindex', '-1');
        destination.focus({ preventScroll: true });
        if (temporary) destination.addEventListener('blur', () => destination.removeAttribute('tabindex'), { once: true });
      }
      destination.scrollIntoView({ block: 'start', behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
    };
    listen(root, 'keydown', () => { root.dataset.keyboard = 'true'; });
    listen(root, 'pointerdown', () => { delete root.dataset.keyboard; });
    if (methods.length) {
      root.querySelector('.equipment').hidden = false;
      selectMethod(methods.some((method) => method.id === location.hash.slice(1)) ? location.hash.slice(1) : methods[0].id);
      methodButtons.forEach((button) => {
        listen(button, 'focus', () => revealEquipment(button));
        listen(button, 'click', () => { selectMethod(button.dataset.method); history.replaceState(null, '', '#' + button.dataset.method); });
      });
    }
    if (paths.length) {
      root.querySelector('.path-choices').hidden = false;
      choosePath(location.hash === '#path-thaw' ? 'path-thaw' : 'path-cook');
      pathButtons.forEach((button) => listen(button, 'click', () => { choosePath(button.dataset.path); history.replaceState(null, '', '#' + button.dataset.path); }));
    }
    listen(root, 'click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      let target;
      try { target = find(decodeURIComponent(link.hash.slice(1))); } catch { return; }
      if (!target) return;
      event.preventDefault(); // Let the theme's shared smooth-scroll handler see that this jump is handled.
      history.pushState(null, '', link.hash);
      openTarget(target, true, event.detail > 0);
    }, { capture: true });
    const restoreHash = () => {
      if (!location.hash) {
        if (methods.length) selectMethod(methods[0].id);
        if (paths.length) choosePath(paths[0].id);
        return; // Native history restores the page scroll position.
      }
      const target = hashTarget();
      if (target) openTarget(target);
    };
    listen(window, 'hashchange', restoreHash);
    listen(window, 'popstate', restoreHash);
    const watch = root.querySelector('.watch');
    if (watch) {
      let loaded = false;
      listen(watch, 'toggle', () => {
        if (!watch.open || loaded) return;
        loaded = true;
        const iframe = document.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/RzCOSNssxdE';
        iframe.title = 'The Hatch Chile Store roasting demonstration';
        iframe.allow = 'encrypted-media; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        watch.querySelector('.video-frame').replaceChildren(iframe);
      });
    }
    const recipes = root.querySelector('.recipes');
    const controls = root.querySelector('.recipe-controls');
    if (recipes && controls) {
      const cards = [...recipes.querySelectorAll('.recipe')];
      const previous = controls.querySelector('.recipe-prev');
      const next = controls.querySelector('.recipe-next');
      let index = 0;
      const update = () => {
        if (!cards.length) return;
        index = cards.reduce((closest, card, position) => Math.abs(card.offsetLeft - cards[0].offsetLeft - recipes.scrollLeft) < Math.abs(cards[closest].offsetLeft - cards[0].offsetLeft - recipes.scrollLeft) ? position : closest, 0);
        controls.querySelector('.recipe-position').textContent = `${index + 1} / ${cards.length}`;
        previous.disabled = index === 0;
        next.disabled = index === cards.length - 1;
      };
      const move = (delta, event) => {
        index = Math.max(0, Math.min(cards.length - 1, index + delta));
        recipes.scrollTo({ left: cards[index].offsetLeft - cards[0].offsetLeft, behavior: event.detail === 0 || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      listen(previous, 'click', (event) => move(-1, event));
      listen(next, 'click', (event) => move(1, event));
      listen(recipes, 'scroll', update, { passive: true });
      const mobile = matchMedia('(max-width:600px)');
      const resize = () => { controls.hidden = !mobile.matches; update(); };
      listen(mobile, 'change', resize);
      listen(window, 'resize', resize, { passive: true });
      resize();
    }
    if (location.hash) restoreHash();
    listen(document, 'shopify:section:unload', (event) => { if (event.target.contains(root)) abort.abort(); });
  };
  const initializeWithin = (container) => {
    if (container.matches?.('.hcs-cooking-guide')) initialize(container);
    container.querySelectorAll('.hcs-cooking-guide').forEach(initialize);
  };
  initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
